/**
 * Build mode (`?build` in the offline game): lay out towns from the scrap-and-salvage kit.
 *
 * Pick a piece from the panel, move the mouse to see it on the ground (green where it fits, red
 * where it doesn't), click to place. R turns it, Delete removes the selected piece, WASD moves the
 * camera, Ctrl+Z undoes. The Path brush paints dirt paths; the Clear brush marks tiles to clear of
 * trees, rocks and old clutter. Everything saves to this browser as you go; "Apply" reloads the
 * game with your town in it for real (walls block, banks bank). "Download" saves towns.json to
 * send in, so it goes into the shared map (data/maps/towns.json).
 */
import * as THREE from 'three';
import {
  FLAG_WATER, PIECES, PIECE_KINDS, pieceScale, upscaleTowns, isWall, wallJoins, addedAt, emptyTowns, footprint, isPieceKind, type GameMap, type PieceGroup, type PieceKind, type TownFile, type TownPiece,
} from '@sunderfen/shared';
import { BUILDERS } from '../render/models.js';
import { wallPiece } from '../render/town-models.js';
import type { WorldView } from '../render/world-view.js';

export interface TownHost {
  /** The layer to start from (this browser's saved one, or the shared one). */
  initial: TownFile;
  save(t: TownFile): void;
  /** Forget this browser's layer (back to the shared one). */
  reset(): void;
  /** The shared layer (data/maps/towns.json). */
  shared?: TownFile;
}
type Tool = { kind: 'place'; piece: PieceKind } | { kind: 'select' } | { kind: 'path' } | { kind: 'clear' };

const GROUPS: PieceGroup[] = ['Homes', 'Work', 'Walls', 'Props', 'Country'];
const css = `
#build{position:absolute;left:12px;top:64px;bottom:12px;width:250px;display:flex;flex-direction:column;gap:8px;padding:12px;z-index:30;overflow:hidden;font-size:13px}
#build h3{margin:0;font-family:'Alegreya SC',serif;font-size:18px;color:#f1bf55}
#build .tabs{display:flex;gap:4px;flex-wrap:wrap}
#build .list{flex:1;overflow:auto;display:flex;flex-direction:column;gap:3px;padding-right:4px}
#build .pc{display:flex;justify-content:space-between;gap:6px;padding:5px 8px;border-radius:6px;background:rgba(255,255,255,.05);cursor:pointer;border:1px solid transparent;text-align:left;color:inherit;font:inherit}
#build .pc:hover{background:rgba(255,255,255,.1)} #build .pc.on{border-color:#f1bf55;background:rgba(241,191,85,.14)}
#build .pc small{opacity:.6}
#build .row{display:flex;gap:4px;flex-wrap:wrap}
#build .status{font-size:12px;opacity:.85;min-height:32px}
#build .keys{font-size:11.5px;opacity:.6;line-height:1.4}
#build .head{display:flex;align-items:center;justify-content:space-between;gap:8px}
#build .head .min-btn{display:none}
#build-bar{position:absolute;left:50%;transform:translateX(-50%);bottom:84px;z-index:31;display:flex;gap:6px;padding:6px;flex-wrap:wrap;justify-content:center;max-width:calc(100vw - 24px)}
#build-bar:empty{display:none}
#build-bar .btn{min-height:40px;min-width:64px;font-size:14px}
#build-bar .hint{align-self:center;font-size:13px;opacity:.85;padding:0 6px}
body.building #panel,body.building #side #panel{display:none}
body.building #quest-track,body.building #chat-form,body.building #log{display:none}
@media (max-width: 760px), (pointer: coarse) {
  #build{left:0;right:0;top:auto;bottom:0;width:auto;max-height:48vh;border-radius:14px 14px 0 0;padding:10px 12px 14px}
  #build.min{max-height:56px}
  #build.min > :not(.head){display:none}
  #build .head .min-btn{display:inline-flex}
  #build .list{display:grid;grid-template-columns:1fr 1fr;gap:4px;align-content:start}
  #build .pc{padding:9px 8px;font-size:13.5px}
  #build .btn.small{min-height:36px;padding:0 12px}
  #build .keys{display:none}
  #build-bar{bottom:calc(48vh + 10px)}
  body.build-min #build-bar{bottom:66px}
  body.building #vitals{display:none}
}
`;

export class BuildMode {
  /** Where the camera looks while building (moves with WASD, not with the player). */
  readonly focus: THREE.Vector3;
  private town: TownFile;
  private tool: Tool = { kind: 'select' };
  private facing = 0;
  private group: PieceGroup = 'Homes';
  private selected: string | null = null;
  private undo: string[] = [];
  private keys = new Set<string>();
  private root = new THREE.Group();
  private ghost: THREE.Group | null = null;
  private ghostKey = '';
  private pad: THREE.Mesh;
  private overlay = new THREE.Group();
  private hoverTile: [number, number] | null = null;
  private lastPath: [number, number] | null = null;
  /** The last wall tile put down: Shift (or Line on a phone) runs a wall on from it. */
  private lastWall: [number, number] | null = null;
  private panel: HTMLElement;
  private status: HTMLElement;
  private nextId = 1;
  /** Touch: the piece waits where you tapped until you press Place. */
  private pending = false;
  /** Path and Clear: rub out instead of paint; draw straight lines from the last tile. */
  private eraser = false;
  private lineMode = false;
  private collapsed = false;
  private bar: HTMLElement;

  constructor(private view: WorldView, private map: GameMap, private host: TownHost, start: THREE.Vector3) {
    this.focus = start.clone();
    this.town = structuredClone(host.initial);
    if (!this.town.pieces.length) this.town.units = 2;
    for (const p of this.town.pieces) { const n = Number(String(p.id).replace(/\D/g, '')); if (n >= this.nextId) this.nextId = n + 1; }
    view.scene.add(this.root); view.scene.add(this.overlay);
    view.setTownVisible(false);
    this.pad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color: 0x6ae06a, transparent: true, opacity: 0.35, depthWrite: false, toneMapped: false }));
    this.pad.renderOrder = 6; this.pad.visible = false; view.scene.add(this.pad);
    const style = document.createElement('style'); style.textContent = css; document.head.appendChild(style);
    this.panel = document.createElement('div'); this.panel.id = 'build'; this.panel.className = 'plate';
    document.querySelector('#app')!.appendChild(this.panel);
    this.status = document.createElement('div');
    this.bar = document.createElement('div'); this.bar.id = 'build-bar'; this.bar.className = 'plate';
    document.querySelector('#app')!.appendChild(this.bar);
    document.body.classList.add('building');
    this.renderPanel();
    this.rebuild();
    window.addEventListener('keydown', e => this.keydown(e), true);
    window.addEventListener('keyup', e => this.keys.delete(e.key.toLowerCase()), true);
  }

  /* ---------------- per frame ---------------- */
  update(dt: number) {
    const yaw = this.view.yaw, sp = 14 * dt;
    const fwd = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw)), right = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
    if (this.keys.has('w')) this.focus.addScaledVector(fwd, sp);
    if (this.keys.has('s')) this.focus.addScaledVector(fwd, -sp);
    if (this.keys.has('a')) this.focus.addScaledVector(right, -sp);
    if (this.keys.has('d')) this.focus.addScaledVector(right, sp);
    this.focus.x = Math.max(2, Math.min(this.map.width - 2, this.focus.x));
    this.focus.z = Math.max(2, Math.min(this.map.height - 2, this.focus.z));
    this.focus.y = Math.max(0, this.map.heightAt(this.focus.x, this.focus.z)) + 0.9;
  }

  /** Drag the ground to slide the camera over it (one finger, or the mouse). */
  pan(dx: number, dy: number) {
    const yaw = this.view.yaw, k = this.view.dist * 0.0024;
    const fwd = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw)), right = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
    this.focus.addScaledVector(right, -dx * k).addScaledVector(fwd, dy * k);
  }

  /* ---------------- input ---------------- */
  hover(clientX: number, clientY: number) {
    if (this.pending) return; // a tapped piece stays put until placed or cancelled
    const hit = this.view.pick(clientX, clientY);
    this.hoverTile = hit?.tile ?? null;
    this.drawGhost();
  }
  click(clientX: number, clientY: number, shift: boolean, alt = false, touch = false) {
    const hit = this.view.pick(clientX, clientY);
    const t = hit?.tile;
    if (!t) return;
    const tool = this.tool;
    shift ||= this.lineMode; alt ||= this.eraser;
    if (tool.kind === 'place' && isWall(tool.piece) && (shift || touch && this.lineMode)) {
      this.wallRun(tool.piece, t); return;
    }
    if (tool.kind === 'place') {
      // on a phone, a tap shows the piece there; Place puts it down
      if (touch) { this.pending = true; this.hoverTile = t; this.drawGhost(); this.renderBar(); const p = this.ghostPiece(); this.say(p && this.problem(p) ? this.problem(p)! : 'Tap Place to put it here, or tap somewhere else.'); return; }
      this.hoverTile = t; this.placeHere();
    } else if (tool.kind === 'select') {
      const p = this.pieceAt(t[0], t[1]);
      this.selected = p?.id ?? null;
      this.say(p ? `${PIECES[p.kind].name} selected.` : 'Nothing placed there.');
      this.rebuild(); this.renderBar();
    } else if (tool.kind === 'path') {
      const tiles = shift && this.lastPath ? line(this.lastPath, t) : [t];
      const on = !alt; // Alt-click rubs paths out
      this.change(() => {
        for (const [x, z] of tiles) {
          const i = this.town.paths.findIndex(([a, b]) => a === x && b === z);
          if (on && i < 0) this.town.paths.push([x, z]); else if (!on && i >= 0) this.town.paths.splice(i, 1);
        }
      });
      this.lastPath = t;
    } else if (tool.kind === 'clear') {
      this.change(() => {
        const i = this.town.clears.findIndex(([a, b]) => a === t[0] && b === t[1]);
        if (alt && i >= 0) this.town.clears.splice(i, 1); else if (!alt && i < 0) this.town.clears.push([t[0], t[1]]);
      });
      if (alt) { this.say('Unmarked.'); return; }
      const o = this.map.objAt[this.map.idx(t[0], t[1])];
      this.say(o >= 0 ? `Marked: the ${this.map.objById.get(o)!.kind} here goes when you apply.` : 'Marked (nothing stands there now).');
    }
  }
  private ghostPiece(): TownPiece | null {
    if (this.tool.kind !== 'place' || !this.hoverTile) return null;
    const p: TownPiece = { id: 'ghost', kind: this.tool.piece, x: 0, z: 0, facing: this.facing };
    return Object.assign(p, this.anchor(p, this.hoverTile));
  }
  /** Put the piece down where the ghost is. */
  private placeHere() {
    const g = this.ghostPiece(); if (!g) return;
    const p: TownPiece = { ...g, id: `t${this.nextId++}` };
    const bad = this.problem(p);
    if (bad) { this.say(bad); return; }
    this.pending = false;
    if (isWall(p.kind)) this.lastWall = [p.x, p.z];
    this.change(() => this.town.pieces.push(p));
    this.say(`Placed ${PIECES[p.kind].name.toLowerCase()}.${this.clearsUnder(p) ? ` It will clear ${this.clearsUnder(p)} thing${this.clearsUnder(p) > 1 ? 's' : ''} out of the way.` : ''}`);
  }
  /** Run a wall from the last wall tile to `t` (round the corner, so every tile touches the next), past anything in the way. */
  private wallRun(kind: PieceKind, t: [number, number]) {
    const tiles = this.lastWall ? line4(this.lastWall, t) : [t];
    const added: TownPiece[] = [];
    let skipped = 0;
    this.change(() => {
      for (const [x, z] of tiles) {
        const here = this.pieceAt(x, z);
        if (here && isWall(here.kind)) continue;
        const p: TownPiece = { id: `t${this.nextId++}`, kind, x, z, facing: this.facing };
        if (this.problem(p)) { skipped++; continue; }
        this.town.pieces.push(p); added.push(p);
      }
    });
    this.lastWall = t;
    this.say(`${added.length ? `Built ${added.length} tile${added.length > 1 ? 's' : ''} of ${PIECES[kind].name.toLowerCase()}.` : 'Nothing new to build there.'}${skipped ? ` Went round ${skipped > 1 ? `${skipped} tiles that were` : 'a tile that was'} in the way.` : ''} Keep going from here.`);
  }
  /** Turn the piece in hand, or the selected one. */
  private turn() {
    if (this.tool.kind === 'place') { this.facing = (this.facing + 1) % 4; this.drawGhost(); const p = this.ghostPiece(); if (this.pending && p) this.say(this.problem(p) ?? 'Tap Place to put it here.'); return; }
    const p = this.selectedPiece();
    if (p) { const turned = { ...p, facing: (p.facing + 1) % 4 }; const bad = this.problem(turned, p.id); if (bad) this.say(bad); else this.change(() => { p.facing = turned.facing; }); }
  }
  private remove() { const p = this.selectedPiece(); if (p) { this.selected = null; this.change(() => { this.town.pieces = this.town.pieces.filter(x => x !== p); }); this.say('Removed.'); } }
  private pickUp() { const p = this.selectedPiece(); if (p) { this.selected = null; this.change(() => { this.town.pieces = this.town.pieces.filter(x => x !== p); }); this.facing = p.facing; this.setTool({ kind: 'place', piece: p.kind }); this.say('Picked up. Tap or click where it goes.'); } }
  private undoOnce() { const prev = this.undo.pop(); if (prev) { this.town = JSON.parse(prev); this.host.save(this.town); this.selected = null; this.rebuild(); this.renderPanel(); this.say('Undone.'); } }
  private cancel() { this.pending = false; this.selected = null; this.setTool({ kind: 'select' }); this.rebuild(); }

  private keydown(e: KeyboardEvent) {
    if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
    const k = e.key.toLowerCase();
    const handled = () => { e.preventDefault(); e.stopImmediatePropagation(); };
    if (['w', 'a', 's', 'd'].includes(k)) { this.keys.add(k); return handled(); }
    if (k === 'r') { handled(); this.turn(); return; }
    if (k === 'delete' || k === 'backspace') { handled(); this.remove(); return; }
    if (k === 'm') { handled(); this.pickUp(); return; }
    if (k === 'escape') { handled(); this.cancel(); return; }
    if (k === 'z' && (e.ctrlKey || e.metaKey)) { handled(); this.undoOnce(); return; }
    if (k === 'enter' && this.pending) { handled(); this.placeHere(); return; }
  }

  /* ---------------- the layer ---------------- */
  private change(fn: () => void) {
    this.undo.push(JSON.stringify(this.town)); if (this.undo.length > 100) this.undo.shift();
    fn();
    this.host.save(this.town);
    this.rebuild();
    this.renderPanel();
  }
  private selectedPiece() { return this.town.pieces.find(p => p.id === this.selected) ?? null; }
  /** The tiles a piece takes, its own and the stations that come with it. */
  private tilesOf(p: Pick<TownPiece, 'kind' | 'x' | 'z' | 'facing'>) {
    const f = footprint(p), out: [number, number][] = [];
    for (let z = f.z; z < f.z + f.h; z++) for (let x = f.x; x < f.x + f.w; x++) out.push([x, z]);
    for (const a of PIECES[p.kind].adds ?? []) out.push(addedAt(p, a));
    return out;
  }
  private pieceAt(x: number, z: number) { return [...this.town.pieces].reverse().find(p => this.tilesOf(p).some(([a, b]) => a === x && b === z)) ?? null; }
  /** Put the footprint's centre under the mouse. */
  private anchor(p: TownPiece, t: [number, number]) { const f = footprint({ ...p, x: 0, z: 0 }); return { x: t[0] - Math.floor((f.w - 1) / 2), z: t[1] - Math.floor((f.h - 1) / 2) }; }
  /** Why a piece can't go there, or null if it can. */
  private problem(p: TownPiece, ignore?: string) {
    for (const [x, z] of this.tilesOf(p)) {
      if (!this.map.inBounds(x, z)) return 'That runs off the edge of the world.';
      if (this.map.flags[this.map.idx(x, z)] & FLAG_WATER) return 'Not on water.';
      const o = this.pieceAt(x, z);
      if (o && o.id !== ignore && o.id !== p.id) return `That overlaps the ${PIECES[o.kind].name.toLowerCase()}.`;
    }
    return null;
  }
  /** How many generated things a piece would clear away. */
  private clearsUnder(p: TownPiece) {
    const ids = new Set<number>();
    for (const [x, z] of this.tilesOf(p)) { if (!this.map.inBounds(x, z)) continue; const o = this.map.objAt[this.map.idx(x, z)]; if (o >= 0 && !this.map.objById.get(o)!.props?.town) ids.add(o); }
    return ids.size;
  }

  /* ---------------- drawing ---------------- */
  /** A piece's model in world space, its stations included. */
  private model(p: Pick<TownPiece, 'id' | 'kind' | 'x' | 'z' | 'facing'>, joins?: Map<string, number>) {
    const d = PIECES[p.kind], f = footprint(p), g = new THREE.Group();
    if (isWall(p.kind)) {
      const join = (joins ?? wallJoins([...this.town.pieces.filter(q => q.id !== p.id), p])).get(p.id) ?? 0;
      const m = new THREE.Group(); wallPiece(m, p.kind, join, p.facing, p.x, p.z);
      m.position.set(p.x + 0.5, Math.max(0, this.map.heightAt(p.x + 0.5, p.z + 0.5)), p.z + 0.5); m.scale.set(...pieceScale(p.kind));
      g.add(m); return g;
    }
    const at = (x: number, z: number, key: string, rot: number) => {
      const b = BUILDERS[key]; if (!b) return;
      const m = new THREE.Group(); b(m);
      m.position.set(x, Math.max(0, this.map.heightAt(x, z)), z); m.rotation.y = rot; m.scale.set(...pieceScale(key));
      g.add(m);
    };
    const rot = (p.facing % 4) * Math.PI / 2;
    at(f.x + f.w / 2, f.z + f.h / 2, d.type === 'station' ? d.station! : p.kind, rot);
    for (const a of d.adds ?? []) { const [x, z] = addedAt(p, a); at(x + 0.5, z + 0.5, a.kind, rot); }
    return g;
  }
  private rebuild() {
    for (const c of [...this.root.children]) this.root.remove(c);
    const joins = wallJoins(this.town.pieces);
    for (const p of this.town.pieces) {
      const m = this.model(p, joins);
      if (p.id === this.selected) m.add(this.outline(this.tilesOf(p), 0xf1bf55));
      this.root.add(m);
    }
    // painted paths and marked clears
    for (const c of [...this.overlay.children]) this.overlay.remove(c);
    const quad = (x: number, z: number, color: number, op: number, s = 0.96) => {
      const q = new THREE.Mesh(new THREE.PlaneGeometry(s, s).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: op, depthWrite: false, toneMapped: false }));
      q.position.set(x + 0.5, Math.max(0, this.map.heightAt(x + 0.5, z + 0.5)) + 0.04, z + 0.5); q.renderOrder = 5; this.overlay.add(q);
    };
    // what the generator put under the pieces and on cleared tiles goes when you apply: hide it now
    const under = new Set<number>();
    const mark = (x: number, z: number) => { if (!this.map.inBounds(x, z)) return; const o = this.map.objAt[this.map.idx(x, z)]; if (o >= 0 && !this.map.objById.get(o)!.props?.town) under.add(o); };
    for (const p of this.town.pieces) for (const [x, z] of this.tilesOf(p)) mark(x, z);
    for (const [x, z] of this.town.clears) mark(x, z);
    this.view.hideUnder(under);
    for (const [x, z] of this.town.paths) quad(x, z, 0xa88a5a, 0.85);
    for (const [x, z] of this.town.clears) quad(x, z, 0xe0402a, 0.45, 0.7);
    this.drawGhost();
  }
  private outline(tiles: [number, number][], color: number) {
    const g = new THREE.Group();
    for (const [x, z] of tiles) {
      const q = new THREE.Mesh(new THREE.PlaneGeometry(0.98, 0.98).rotateX(-Math.PI / 2), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.35, depthWrite: false, toneMapped: false }));
      q.position.set(x + 0.5, Math.max(0, this.map.heightAt(x + 0.5, z + 0.5)) + 0.05, z + 0.5); q.renderOrder = 6; g.add(q);
    }
    return g;
  }
  private drawGhost() {
    const t = this.hoverTile, tool = this.tool;
    if (this.ghost) { this.view.scene.remove(this.ghost); this.ghost = null; }
    this.pad.visible = false;
    if (!t) return;
    if (tool.kind !== 'place') {
      this.pad.visible = true; this.pad.scale.set(1, 1, 1);
      this.pad.position.set(t[0] + 0.5, Math.max(0, this.map.heightAt(t[0] + 0.5, t[1] + 0.5)) + 0.06, t[1] + 0.5);
      (this.pad.material as THREE.MeshBasicMaterial).color.setHex(tool.kind === 'path' ? 0xc8a060 : tool.kind === 'clear' ? 0xe0402a : 0xf1bf55);
      return;
    }
    const p: TownPiece = { id: 'ghost', kind: tool.piece, x: 0, z: 0, facing: this.facing };
    Object.assign(p, this.anchor(p, t));
    const g = this.model(p);
    g.traverse((c: THREE.Object3D) => { const m = c as THREE.Mesh; if (m.isMesh) { m.material = (m.material as THREE.Material).clone(); Object.assign(m.material, { transparent: true, opacity: 0.6, depthWrite: false }); m.castShadow = false; } });
    g.add(this.outline(this.tilesOf(p), this.problem(p) ? 0xe0402a : 0x6ae06a));
    this.ghost = g; this.view.scene.add(g);
  }

  /* ---------------- panel ---------------- */
  private setTool(t: Tool) { this.tool = t; this.ghostKey = ''; this.lastPath = null; this.pending = false; if (t.kind !== 'select') this.selected = null; this.renderPanel(); this.drawGhost(); }
  private say(s: string) { this.status.textContent = s; }
  private renderPanel() {
    const el = this.panel;
    el.innerHTML = '';
    const btn = (label: string, on: boolean, fn: () => void, cls = 'btn small') => { const b = document.createElement('button'); b.type = 'button'; b.className = cls + (on ? ' primary' : ''); b.textContent = label; b.onclick = fn; return b; };
    el.classList.toggle('min', this.collapsed); document.body.classList.toggle('build-min', this.collapsed);
    const head = document.createElement('div'); head.className = 'head';
    const h = document.createElement('h3'); h.textContent = this.tool.kind === 'place' && this.collapsed ? PIECES[this.tool.piece].name : 'Build mode';
    head.append(h, btn(this.collapsed ? 'Pieces ▴' : 'Hide ▾', false, () => { this.collapsed = !this.collapsed; this.renderPanel(); }, 'btn small min-btn'));
    el.appendChild(head);
    if (this.town.units !== 2 && this.town.pieces.length) {
      const note = document.createElement('div'); note.className = 'status';
      note.textContent = 'This town was laid out before buildings went to their real size, so its pieces overlap now.';
      el.appendChild(note);
      const fix = document.createElement('div'); fix.className = 'row';
      fix.append(
        btn('Start from the new Meadowfold', false, () => { this.host.reset(); location.reload(); }, 'btn small primary'),
        btn('Spread mine out', false, () => { this.change(() => { this.town = upscaleTowns(this.town); }); this.say('Spread out to the new sizes. Check it over, then Apply.'); }),
      );
      el.appendChild(fix);
    }
    const tools = document.createElement('div'); tools.className = 'row';
    tools.append(
      btn('Select', this.tool.kind === 'select', () => this.setTool({ kind: 'select' })),
      btn('Path', this.tool.kind === 'path', () => this.setTool({ kind: 'path' })),
      btn('Clear', this.tool.kind === 'clear', () => this.setTool({ kind: 'clear' })),
    );
    el.appendChild(tools);
    const go = document.createElement('select'); go.className = 'btn small'; go.style.width = '100%';
    go.innerHTML = '<option value="">Go to…</option>' + this.map.regions.filter(r => r.label).map(r => `<option value="${r.key}">${r.name}</option>`).join('');
    go.onchange = () => { const r = this.map.regions.find(x => x.key === go.value); if (r?.label) { this.focus.set(r.label[0], 0, r.label[1]); this.say(`Over ${r.name}.`); } go.value = ''; };
    el.appendChild(go);
    const tabs = document.createElement('div'); tabs.className = 'tabs';
    for (const g of GROUPS) tabs.appendChild(btn(g, this.group === g, () => { this.group = g; this.renderPanel(); }));
    el.appendChild(tabs);
    const list = document.createElement('div'); list.className = 'list';
    for (const k of PIECE_KINDS.filter(k => PIECES[k].group === this.group)) {
      const d = PIECES[k], b = document.createElement('button'); b.type = 'button';
      b.className = 'pc' + (this.tool.kind === 'place' && this.tool.piece === k ? ' on' : '');
      b.innerHTML = `<span></span><small>${d.w}×${d.h}</small>`; b.firstElementChild!.textContent = d.name; b.title = d.desc;
      b.onclick = () => { this.setTool({ kind: 'place', piece: k }); if (matchMedia('(max-width: 760px), (pointer: coarse)').matches) { this.collapsed = true; this.renderPanel(); this.say('Tap the ground where it goes.'); } };
      list.appendChild(b);
    }
    el.appendChild(list);
    this.status.className = 'status'; el.appendChild(this.status);
    const acts = document.createElement('div'); acts.className = 'row';
    acts.append(
      btn('Apply', false, () => { this.host.save(this.town); location.reload(); }, 'btn small primary'),
      btn('Download', false, () => this.download()),
      btn('Import', false, () => this.importFile()),
      btn('Reset', false, () => { if (confirm('Forget the town you built in this browser and go back to the shared one?')) { this.host.reset(); location.reload(); } }),
      btn('Exit', false, () => { this.host.save(this.town); setBuild(false); location.reload(); }),
    );
    el.appendChild(acts);
    const keys = document.createElement('div'); keys.className = 'keys';
    keys.textContent = `${this.town.pieces.length} pieces · ${this.town.paths.length} path tiles. Click to place · R turn · M move · Delete remove · drag or WASD to move the camera · Q/E spin · Ctrl+Z undo. Walls join up: Shift-click runs a wall on from the last one. Path and Clear: click to paint, Shift-click for a line, Alt-click to rub out. Apply to walk your town for real.`;
    el.appendChild(keys);
    this.renderBar();
  }

  /** The big buttons along the bottom: what you can do right now (made for thumbs). */
  private renderBar() {
    const bar = this.bar; bar.innerHTML = '';
    const b = (label: string, fn: () => void, primary = false, on = false) => { const x = document.createElement('button'); x.type = 'button'; x.className = 'btn' + (primary || on ? ' primary' : ''); x.textContent = label; x.onclick = fn; bar.appendChild(x); };
    const hint = (t: string) => { const x = document.createElement('span'); x.className = 'hint'; x.textContent = t; bar.appendChild(x); };
    const tool = this.tool;
    if (tool.kind === 'place') {
      const walls = isWall(tool.piece);
      if (walls) b(this.lineMode ? 'Line: on' : 'Line: off', () => { this.lineMode = !this.lineMode; this.pending = false; this.renderBar(); this.say(this.lineMode ? 'Tap along the line of the wall: each tap builds on from the last.' : 'Tap where each piece goes.'); }, false, this.lineMode);
      if (this.pending) b('✓ Place', () => this.placeHere(), true);
      else hint(walls && this.lineMode ? 'Tap to build the wall on to there' : `Tap the ground for the ${PIECES[tool.piece].name.toLowerCase()}`);
      b('⟳ Turn', () => this.turn());
      b('✕ Done', () => this.cancel());
    } else if (tool.kind === 'select' && this.selectedPiece()) {
      b('⟳ Turn', () => this.turn()); b('✥ Move', () => this.pickUp()); b('🗑 Delete', () => this.remove()); b('Done', () => this.cancel());
    } else if (tool.kind === 'path' || tool.kind === 'clear') {
      b(this.eraser ? 'Rubbing out' : 'Painting', () => { this.eraser = !this.eraser; this.renderBar(); }, false, this.eraser);
      b(this.lineMode ? 'Line: on' : 'Line: off', () => { this.lineMode = !this.lineMode; this.lastPath = null; this.renderBar(); }, false, this.lineMode);
      b('Done', () => this.cancel());
    } else if (tool.kind === 'select') hint('Tap a piece to change it, or pick one to place');
    if (this.undo.length) b('↶ Undo', () => this.undoOnce());
  }
  private download() {
    const blob = new Blob([JSON.stringify(this.town, null, 1)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'towns.json'; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    this.say('Saved towns.json. Send it over to put your town in the shared map.');
  }
  private importFile() {
    const inp = document.createElement('input'); inp.type = 'file'; inp.accept = '.json,application/json';
    inp.onchange = () => {
      const f = inp.files?.[0]; if (!f) return;
      void f.text().then(txt => {
        try {
          const t = JSON.parse(txt) as TownFile;
          if (t.version !== 1 || !Array.isArray(t.pieces)) throw new Error('not a towns file');
          t.pieces = t.pieces.filter(p => isPieceKind(p.kind)); t.paths ??= []; t.clears ??= [];
          this.change(() => { this.town = t; });
          this.say(`Imported ${t.pieces.length} pieces. Apply to walk it.`);
        } catch { this.say("That file isn't a towns.json."); }
      });
    };
    inp.click();
  }
}

/** Tiles from a to b where each touches the next side-on (so walls join): along the longer way first, then round the corner. */
function line4(a: [number, number], b: [number, number]): [number, number][] {
  const out: [number, number][] = [[a[0], a[1]]];
  let [x, z] = a;
  const xFirst = Math.abs(b[0] - a[0]) >= Math.abs(b[1] - a[1]);
  const stepX = () => { while (x !== b[0]) { x += Math.sign(b[0] - x); out.push([x, z]); } };
  const stepZ = () => { while (z !== b[1]) { z += Math.sign(b[1] - z); out.push([x, z]); } };
  if (xFirst) { stepX(); stepZ(); } else { stepZ(); stepX(); }
  return out;
}
/** Tiles on a straight line between two tiles. */
function line(a: [number, number], b: [number, number]): [number, number][] {
  const out: [number, number][] = [], n = Math.max(Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1]), 1);
  for (let i = 0; i <= n; i++) out.push([Math.round(a[0] + ((b[0] - a[0]) * i) / n), Math.round(a[1] + ((b[1] - a[1]) * i) / n)]);
  return out;
}
export { emptyTowns };
const BUILD_KEY = 'hollowmere-build';
/** Is build mode on? (`?build` in the address, or switched on in Settings.) */
export function buildOn() { try { return location.search.includes('build') || localStorage.getItem(BUILD_KEY) === '1'; } catch { return location.search.includes('build'); } }
export function setBuild(on: boolean) { try { if (on) localStorage.setItem(BUILD_KEY, '1'); else localStorage.removeItem(BUILD_KEY); } catch { /* ignore */ } }
